import zoomSdk from "@zoom/appssdk";
import { debugLog } from "../api.js";
import { serializeError } from "../serialize-error.js";
import { redactAuthEvent } from "./redact.js";

const AUTHORIZE_TIMEOUT_MS = 30_000;
const FALLBACK_GRACE_MS = 2_000; // after a usable-but-unmatched code, wait briefly for a better one
const RETRY_AFTER_MS = 8_000;    // no event at all by now → re-issue authorize once

function randomStateValue(): string {
  const bytes = new Uint8Array(18);
  crypto.getRandomValues(bytes);
  let str = "";
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Runs the Zoom OAuth authorize step and resolves with the returned code.
 * Acceptance policy: a matching or absent `state` resolves immediately (Mac /
 * already-authorized path). A PRESENT-but-non-matching state (observed on
 * Windows — the client echoes a stale state) is buffered and, if no better
 * event follows within FALLBACK_GRACE_MS, used as a fallback rather than
 * hanging until the 30s timeout. PKCE (codeChallenge/codeVerifier, verified
 * at the server token exchange) is the binding that prevents redeeming an
 * injected/forged code — state is a fast-path optimization, not the security
 * boundary.
 */
export async function runAuthorizeFlow(codeChallenge: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const stateValue = randomStateValue();
    let bufferedCode: string | null = null; // last code seen whose state did not match
    let sawAnyEvent = false;
    let retried = false;
    let graceTimer: ReturnType<typeof setTimeout> | null = null;

    const hardTimeout = setTimeout(() => {
      cleanup();
      if (bufferedCode) {
        void debugLog("authorize.fallback", { reason: "hard-timeout", stateMatched: false });
        resolve(bufferedCode); // server PKCE will reject this if it is not genuinely ours
      } else {
        reject(new Error("authorize timeout — no onAuthorized event in 30s"));
      }
    }, AUTHORIZE_TIMEOUT_MS);

    const retryTimer = setTimeout(() => {
      if (sawAnyEvent || retried) return;
      retried = true;
      void debugLog("authorize.retry", { reason: "no-event" });
      callAuthorize();
    }, RETRY_AFTER_MS);

    function cleanup() {
      clearTimeout(hardTimeout);
      clearTimeout(retryTimer);
      if (graceTimer) clearTimeout(graceTimer);
    }

    const handler = (event: unknown) => {
      const e = event as { code?: string; state?: string };
      sawAnyEvent = true;
      void debugLog("onAuthorized", redactAuthEvent(e, stateValue));
      if (!e.code) return; // nothing usable; keep waiting

      const stateMatches = !e.state || e.state === stateValue;
      if (stateMatches) {
        cleanup();
        resolve(e.code); // Mac / already-authorized happy path — immediate
        return;
      }

      // Present-but-non-matching state (the Windows case). Buffer it and, if no
      // matching event follows shortly, fall back to it. PKCE is the real binding.
      bufferedCode = e.code;
      if (!graceTimer) {
        graceTimer = setTimeout(() => {
          if (bufferedCode) {
            cleanup();
            void debugLog("authorize.fallback", { reason: "grace", stateMatched: false });
            resolve(bufferedCode);
          }
        }, FALLBACK_GRACE_MS);
      }
    };

    function callAuthorize() {
      void debugLog("authorize.call", { stateValue, codeChallengePrefix: codeChallenge.slice(0, 12) });
      zoomSdk
        .authorize({ state: stateValue, codeChallenge })
        .then((res) => void debugLog("authorize.ack", { res }))
        .catch((err) => {
          cleanup();
          void debugLog("authorize.catch", { error: serializeError(err) });
          reject(err);
        });
    }

    // Register the listener BEFORE calling authorize so an early event is not missed.
    (zoomSdk as unknown as { onAuthorized: (cb: (e: unknown) => void) => void }).onAuthorized(
      handler,
    );
    callAuthorize();
  });
}
